import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { formatNoteEditorTimestamp } from '../../utils/dateFormats';
import {
    buildVisibleTextMap,
    clearTxtEditorSyncCssHighlight,
    clearTxtEditorSyncMark,
    findVisibleOffsetsForSelectedText,
    findVisibleTextRangeInRoot,
    getVisibleTextFromRoot,
    normalizeTxtEditorHtml,
    plainTextToEditorDivHtml,
    replaceYoutubeUrlsWithIframes,
    resolveHtmlSourceRangeFromPreviewSelection,
    resolvePreviewRangeFromHtmlSourceSelection,
    resolvePreviewVisibleSyncRange,
    measureTextareaSelectionBand,
    scrollTextareaToSelection,
    wrapRangeWithTxtEditorSyncMark,
} from './txtEditorCore';
import {
    isTxtQuickColorShortcutKey,
    TXT_QUICK_COLOR_KEY_MAP,
    TXT_QUICK_COLOR_PALETTE,
} from './txtEditorShortcuts';

export type TxtSplitEditorHandle = {
    showHtmlSource: boolean;
    txtEditorSelectionRangeRef: React.MutableRefObject<{ start: number; end: number } | null>;
    descriptionTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
    previewContentEditableRef: React.RefObject<HTMLDivElement | null>;
    isPreviewEditableFocused: () => boolean;
    insertTimestamp: () => void;
    insertTimestampInPreviewEditable: () => void;
    insertTimestampInTextarea: () => void;
    togglePreviewSelectionCase: () => void;
    toggleTextareaSelectionCase: () => void;
    applyPreviewInlineStyle: (styleType: 'color' | 'background-color', color: string) => void;
    applyTextareaInlineStyle: (styleType: 'color' | 'background-color', color: string) => void;
    wrapPreviewSelectionCopyable: () => void;
    wrapTextareaSelectionCopyable: () => void;
    focusHtmlSourceAtSyncEnd: () => boolean;
    focusPreviewAtSyncEnd: () => boolean;
};

export type UseTxtSplitEditorOptions = {
    value: string;
    onChange: (value: string) => void;
    onBlur?: (value: string) => void | Promise<void>;
    disabled?: boolean;
    showHtmlSourceStorageKey?: string;
    defaultShowHtmlSource?: boolean;
    /** Alt+G/O/R background, Alt+Shift+G/O/R text (when focus is in preview or HTML source). */
    enableColorShortcuts?: boolean;
    /** Alt+Q insert timestamp (when focus is in preview or HTML source). */
    enableTimestampShortcut?: boolean;
};

export function useTxtSplitEditor({
    value,
    onChange,
    onBlur,
    disabled = false,
    showHtmlSourceStorageKey,
    defaultShowHtmlSource = true,
    enableColorShortcuts = false,
    enableTimestampShortcut = false,
}: UseTxtSplitEditorOptions) {
    const [showHtmlSource, setShowHtmlSource] = useState(defaultShowHtmlSource);
    const txtPreviewRef = useRef<HTMLDivElement | null>(null);
    const previewContentEditableRef = useRef<HTMLDivElement | null>(null);
    const previewEditingFocusedRef = useRef(false);
    const suppressPreviewInputRef = useRef(false);
    const htmlSourceEditingFocusedRef = useRef(false);
    const onChangeRef = useRef(onChange);
    const onBlurRef = useRef(onBlur);
    onChangeRef.current = onChange;
    onBlurRef.current = onBlur;
    const [htmlSourceSyncFromPreview, setHtmlSourceSyncFromPreview] = useState(false);
    const syncSelectionFromPreviewRef = useRef(false);
    const previewPointerActiveRef = useRef(false);
    const descriptionTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const htmlSourceStackRef = useRef<HTMLDivElement | null>(null);
    const pendingHtmlSourceSelectionRef = useRef<{ start: number; end: number } | null>(null);
    const [txtPreviewSelection, setTxtPreviewSelection] = useState<null | { text: string; top: number; left: number }>(null);
    const [txtEditorSelectionRange, setTxtEditorSelectionRangeState] = useState<null | { start: number; end: number }>(null);
    const [htmlSourceScrollTop, setHtmlSourceScrollTop] = useState(0);
    const [htmlSourceSelectionBand, setHtmlSourceSelectionBand] = useState<null | { top: number; height: number }>(null);
    const [htmlSourceTextareaInset, setHtmlSourceTextareaInset] = useState<null | {
        top: number;
        left: number;
        width: number;
        height: number;
    }>(null);
    const txtEditorSelectionRangeRef = useRef<typeof txtEditorSelectionRange>(null);
    txtEditorSelectionRangeRef.current = txtEditorSelectionRange;

    const setTxtEditorSyncRange = useCallback((range: null | { start: number; end: number }) => {
        txtEditorSelectionRangeRef.current = range;
        setTxtEditorSelectionRangeState(range);
    }, []);

    useEffect(() => {
        if (!showHtmlSourceStorageKey) {
            return;
        }
        try {
            const raw = localStorage.getItem(showHtmlSourceStorageKey);
            if (raw === '0') {
                setShowHtmlSource(false);
            } else if (raw === '1') {
                setShowHtmlSource(true);
            }
        } catch {
            /* ignore */
        }
    }, [showHtmlSourceStorageKey]);

    useEffect(() => {
        if (!showHtmlSource || htmlSourceEditingFocusedRef.current || previewEditingFocusedRef.current) {
            return;
        }
        const formatted = normalizeTxtEditorHtml(value);
        if (formatted !== value) {
            onChangeRef.current(formatted);
        }
    }, [showHtmlSource, value]);

    const handleToggleHtmlSource = useCallback(() => {
        setShowHtmlSource((prev) => {
            const next = !prev;
            if (showHtmlSourceStorageKey) {
                try {
                    localStorage.setItem(showHtmlSourceStorageKey, next ? '1' : '0');
                } catch {
                    /* ignore */
                }
            }
            if (!next) {
                setTxtEditorSyncRange(null);
                clearTxtEditorSyncCssHighlight();
            }
            return next;
        });
    }, [showHtmlSourceStorageKey]);

    const hideTxtPreviewSelection = useCallback(() => setTxtPreviewSelection(null), []);

    const clearPreviewEditorSync = useCallback(() => {
        setTxtEditorSyncRange(null);
        setHtmlSourceSyncFromPreview(false);
        setHtmlSourceSelectionBand(null);
        hideTxtPreviewSelection();
        clearTxtEditorSyncCssHighlight();
        clearTxtEditorSyncMark(previewContentEditableRef.current);
    }, [hideTxtPreviewSelection]);

    const normalizedValueHtml = useMemo(() => normalizeTxtEditorHtml(value || ''), [value]);

    const measureHtmlSourceTextareaInset = useCallback(() => {
        const stack = htmlSourceStackRef.current;
        const ta = descriptionTextareaRef.current;
        if (!stack || !ta) {
            return null;
        }
        const stackRect = stack.getBoundingClientRect();
        const taRect = ta.getBoundingClientRect();
        return {
            top: taRect.top - stackRect.top,
            left: taRect.left - stackRect.left,
            width: taRect.width,
            height: taRect.height,
        };
    }, []);

    const refreshHtmlSourceSelectionBand = useCallback((range?: null | { start: number; end: number }) => {
        const ta = descriptionTextareaRef.current;
        const sync = range !== undefined ? range : txtEditorSelectionRangeRef.current;
        if (!ta || !sync || sync.start === sync.end) {
            setHtmlSourceSelectionBand(null);
            return;
        }
        const start = Math.min(sync.start, sync.end);
        const end = Math.max(sync.start, sync.end);
        setHtmlSourceSelectionBand(measureTextareaSelectionBand(ta, start, end));
        setHtmlSourceScrollTop(ta.scrollTop);
        setHtmlSourceTextareaInset(measureHtmlSourceTextareaInset());
    }, [measureHtmlSourceTextareaInset]);

    const showHtmlSourceSyncOverlay =
        showHtmlSource &&
        htmlSourceSyncFromPreview &&
        htmlSourceSelectionBand != null &&
        htmlSourceTextareaInset != null;

    const syncPreviewEditableHtml = useCallback(() => {
        const el = previewContentEditableRef.current;
        if (!el || previewEditingFocusedRef.current) {
            return;
        }
        const html = normalizedValueHtml.trim() || '<p><br></p>';
        if (el.innerHTML !== html) {
            el.innerHTML = html;
        }
    }, [normalizedValueHtml]);

    useLayoutEffect(() => {
        syncPreviewEditableHtml();
    }, [syncPreviewEditableHtml]);

    useLayoutEffect(() => {
        if (!htmlSourceSyncFromPreview || !txtEditorSelectionRange) {
            if (!htmlSourceSyncFromPreview) {
                setHtmlSourceSelectionBand(null);
            }
            return;
        }
        refreshHtmlSourceSelectionBand();
    }, [htmlSourceSyncFromPreview, refreshHtmlSourceSelectionBand, txtEditorSelectionRange, value]);

    useEffect(() => {
        if (!htmlSourceSyncFromPreview) {
            return;
        }
        const stack = htmlSourceStackRef.current;
        if (!stack) {
            return;
        }
        const observer = new ResizeObserver(() => {
            refreshHtmlSourceSelectionBand();
        });
        observer.observe(stack);
        return () => observer.disconnect();
    }, [htmlSourceSyncFromPreview, refreshHtmlSourceSelectionBand]);

    const handlePreviewEditableInput = useCallback(() => {
        if (suppressPreviewInputRef.current) {
            return;
        }
        const el = previewContentEditableRef.current;
        if (!el) {
            return;
        }
        clearPreviewEditorSync();
        const normalized = normalizeTxtEditorHtml(el.innerHTML);
        if (normalized !== value) {
            onChangeRef.current(normalized);
        }
    }, [clearPreviewEditorSync, value]);

    const handlePreviewCut = useCallback(() => {
        clearPreviewEditorSync();
    }, [clearPreviewEditorSync]);

    const handlePreviewMouseDown = useCallback(() => {
        previewPointerActiveRef.current = true;
        pendingHtmlSourceSelectionRef.current = null;
    }, []);

    const handlePreviewEditableFocus = useCallback(() => {
        previewEditingFocusedRef.current = true;
        pendingHtmlSourceSelectionRef.current = null;
        hideTxtPreviewSelection();
        clearTxtEditorSyncCssHighlight();
    }, [hideTxtPreviewSelection]);

    const handlePreviewEditableBlur = useCallback(() => {
        previewEditingFocusedRef.current = false;
        setHtmlSourceSyncFromPreview(false);
        const el = previewContentEditableRef.current;
        if (!el) {
            return;
        }
        const normalized = normalizeTxtEditorHtml(el.innerHTML);
        if (normalized !== value) {
            onChangeRef.current(normalized);
            void onBlurRef.current?.(normalized);
        }
        const displayHtml = normalized.trim() || '<p><br></p>';
        if (el.innerHTML !== displayHtml) {
            suppressPreviewInputRef.current = true;
            el.innerHTML = displayHtml;
            suppressPreviewInputRef.current = false;
        }
    }, [value]);

    const handlePreviewKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled || event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
                return;
            }

            event.preventDefault();
            const el = previewContentEditableRef.current;
            if (!el) {
                return;
            }

            el.focus();
            const inserted = document.execCommand('insertParagraph');
            if (!inserted) {
                document.execCommand('insertHTML', false, '<div><br></div>');
            }
            handlePreviewEditableInput();
        },
        [disabled, handlePreviewEditableInput],
    );

    const handlePreviewEditablePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            try {
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (!text) {
                    return;
                }
                const transformed = replaceYoutubeUrlsWithIframes(text);
                const el = previewContentEditableRef.current;
                if (!el) {
                    return;
                }
                e.preventDefault();
                el.focus();
                if (transformed !== text) {
                    document.execCommand('insertHTML', false, transformed);
                } else {
                    document.execCommand('insertHTML', false, plainTextToEditorDivHtml(text));
                }
                handlePreviewEditableInput();
            } catch {
                /* fallback to default paste */
            }
        },
        [handlePreviewEditableInput],
    );

    const isPreviewEditableFocused = useCallback((): boolean => {
        const root = previewContentEditableRef.current;
        if (!root || disabled) {
            return false;
        }
        const active = document.activeElement;
        if (!active) {
            return false;
        }
        return root === active || root.contains(active);
    }, [disabled]);

    const mutatePreviewEditableSelection = useCallback(
        (mutator: (ctx: { range: Range; selected: string; collapsed: boolean }) => void): boolean => {
            const root = previewContentEditableRef.current;
            if (!root) {
                return false;
            }
            root.focus();
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0) {
                return false;
            }
            const range = sel.getRangeAt(0);
            if (!root.contains(range.commonAncestorContainer)) {
                return false;
            }
            mutator({ range, selected: sel.toString(), collapsed: sel.isCollapsed });
            handlePreviewEditableInput();
            return true;
        },
        [handlePreviewEditableInput],
    );

    const insertTimestampInPreviewEditable = useCallback(() => {
        const date = formatNoteEditorTimestamp();
        const html = `<span style='color:#40E0D0; font-size:8px;'>${date}</span>`;
        mutatePreviewEditableSelection(({ range, collapsed }) => {
            if (collapsed) {
                range.collapse(true);
            }
            const fragment = range.createContextualFragment(html);
            range.deleteContents();
            range.insertNode(fragment);
            range.collapse(false);
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        });
    }, [mutatePreviewEditableSelection]);

    const togglePreviewSelectionCase = useCallback(() => {
        mutatePreviewEditableSelection(({ range, selected, collapsed }) => {
            if (collapsed || !selected.trim()) {
                return;
            }
            const isUpperCase =
                selected === selected.toUpperCase() && selected !== selected.toLowerCase();
            const nextText = isUpperCase ? selected.toLowerCase() : selected.toUpperCase();
            range.deleteContents();
            range.insertNode(document.createTextNode(nextText));
        });
    }, [mutatePreviewEditableSelection]);

    const wrapPreviewSelectionCopyable = useCallback(() => {
        mutatePreviewEditableSelection(({ range, selected, collapsed }) => {
            const inner = selected || 'text to copy';
            const wrapped = `[[${inner}]]`;
            if (collapsed) {
                range.collapse(true);
            }
            range.deleteContents();
            range.insertNode(document.createTextNode(wrapped));
        });
    }, [mutatePreviewEditableSelection]);

    const applyPreviewInlineStyle = useCallback(
        (styleType: 'color' | 'background-color', color: string) => {
            mutatePreviewEditableSelection(({ range, selected, collapsed }) => {
                const content = (selected || 'text').trim() || 'text';
                const span = document.createElement('span');
                span.setAttribute('style', `${styleType}: ${color}`);
                span.textContent = content;
                if (collapsed) {
                    range.collapse(true);
                }
                range.deleteContents();
                range.insertNode(span);
            });
        },
        [mutatePreviewEditableSelection],
    );

    const applyToTextareaSelection = useCallback(
        (fn: (args: {
            value: string;
            start: number;
            end: number;
            selected: string;
        }) => { nextValue: string; nextStart?: number; nextEnd?: number }) => {
            const el = descriptionTextareaRef.current;
            if (!el) {
                return;
            }
            const currentValue = el.value ?? '';
            const start = typeof el.selectionStart === 'number' ? el.selectionStart : currentValue.length;
            const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : currentValue.length;
            const selected = currentValue.slice(start, end);
            const { nextValue, nextStart, nextEnd } = fn({ value: currentValue, start, end, selected });
            onChange(nextValue);
            window.setTimeout(() => {
                try {
                    const el2 = descriptionTextareaRef.current;
                    if (!el2) {
                        return;
                    }
                    if (typeof nextStart === 'number' && typeof nextEnd === 'number') {
                        el2.setSelectionRange(nextStart, nextEnd);
                    }
                    el2.focus();
                } catch {
                    /* ignore */
                }
            }, 0);
        },
        [onChange],
    );

    const insertTimestampInTextarea = useCallback(() => {
        const date = formatNoteEditorTimestamp();
        const html = `<span style='color:#40E0D0; font-size:8px;'>${date}</span>`;
        applyToTextareaSelection(({ value: currentValue, start, end }) => {
            const nextValue = currentValue.slice(0, start) + html + currentValue.slice(end);
            const cursor = start + html.length;
            return { nextValue, nextStart: cursor, nextEnd: cursor };
        });
    }, [applyToTextareaSelection]);

    const insertTimestamp = useCallback(() => {
        if (isPreviewEditableFocused()) {
            insertTimestampInPreviewEditable();
            return;
        }
        const ta = descriptionTextareaRef.current;
        if (showHtmlSource && ta && document.activeElement === ta) {
            insertTimestampInTextarea();
            return;
        }
        insertTimestampInPreviewEditable();
    }, [
        insertTimestampInPreviewEditable,
        insertTimestampInTextarea,
        isPreviewEditableFocused,
        showHtmlSource,
    ]);

    const toggleTextareaSelectionCase = useCallback(() => {
        applyToTextareaSelection(({ value: currentValue, start, end, selected }) => {
            if (!selected) {
                return { nextValue: currentValue };
            }
            const isUpperCase =
                selected === selected.toUpperCase() && selected !== selected.toLowerCase();
            const nextText = isUpperCase ? selected.toLowerCase() : selected.toUpperCase();
            const nextValue = currentValue.slice(0, start) + nextText + currentValue.slice(end);
            return { nextValue, nextStart: start, nextEnd: start + nextText.length };
        });
    }, [applyToTextareaSelection]);

    const wrapTextareaSelectionCopyable = useCallback(() => {
        applyToTextareaSelection(({ value: currentValue, start, end, selected }) => {
            const inner = selected || 'text to copy';
            const wrapped = `[[${inner}]]`;
            const nextValue = currentValue.slice(0, start) + wrapped + currentValue.slice(end);
            return { nextValue, nextStart: start + 2, nextEnd: start + 2 + inner.length };
        });
    }, [applyToTextareaSelection]);

    const applyTextareaInlineStyle = useCallback(
        (styleType: 'color' | 'background-color', color: string) => {
            applyToTextareaSelection(({ value: currentValue, start, end, selected }) => {
                const content = (selected || 'text').trim() || 'text';
                const snippet = `<span style="${styleType}: ${color};">${content}</span>`;
                const nextValue = currentValue.slice(0, start) + snippet + currentValue.slice(end);
                return { nextValue, nextStart: start, nextEnd: start + snippet.length };
            });
        },
        [applyToTextareaSelection],
    );

    useEffect(() => {
        const styleId = 'txt-editor-sync-highlight-style';
        let style = document.getElementById(styleId) as HTMLStyleElement | null;
        if (!style) {
            style = document.createElement('style');
            style.id = styleId;
            document.head.appendChild(style);
        }
        style.textContent = `
      mark.txt-editor-sync-mark {
        color: inherit;
        font-size: 200%;
        line-height: 1.2;
        padding: 0.16em 0.42em;
        margin: 0 0.06em;
        border-radius: 7px;
        background: linear-gradient(
          125deg,
          rgba(34, 211, 238, 0.58) 0%,
          rgba(129, 140, 248, 0.6) 42%,
          rgba(232, 121, 249, 0.58) 100%
        );
        box-shadow:
          0 0 12px rgba(34, 211, 238, 0.72),
          0 0 26px rgba(129, 140, 248, 0.55),
          0 0 44px rgba(232, 121, 249, 0.42),
          inset 0 1px 0 rgba(255, 255, 255, 0.32);
        outline: 1.5px solid rgba(34, 211, 238, 0.65);
        outline-offset: 2px;
        text-shadow:
          0 0 10px rgba(34, 211, 238, 0.95),
          0 0 18px rgba(232, 121, 249, 0.75);
      }
      textarea[data-txt-html-source]::selection {
        background: rgba(34, 211, 238, 0.42);
        color: inherit;
      }
      textarea[data-txt-html-source]:not(:focus)::selection {
        background: rgba(34, 211, 238, 0.32);
        color: inherit;
      }
      textarea[data-txt-html-source]::-moz-selection {
        background: rgba(34, 211, 238, 0.42);
        color: inherit;
      }
      textarea[data-txt-html-source]:not(:focus)::-moz-selection {
        background: rgba(34, 211, 238, 0.32);
        color: inherit;
      }
    `;
    }, []);

    const clearPreviewDomSelection = useCallback(() => {
        try {
            const root = previewContentEditableRef.current;
            const sel = window.getSelection();
            if (!root || !sel || sel.rangeCount === 0) {
                return;
            }
            const anchor = sel.anchorNode;
            const focus = sel.focusNode;
            if (
                (anchor && root.contains(anchor)) ||
                (focus && root.contains(focus))
            ) {
                sel.removeAllRanges();
            }
        } catch {
            /* ignore */
        }
    }, []);

    const updateTxtEditorSyncHighlight = useCallback(() => {
        const ta = descriptionTextareaRef.current;
        if (!ta || !showHtmlSource) {
            setTxtEditorSyncRange(null);
            setHtmlSourceSyncFromPreview(false);
            return;
        }
        const start = ta.selectionStart ?? 0;
        const end = ta.selectionEnd ?? 0;
        if (start === end) {
            setTxtEditorSyncRange(null);
            setHtmlSourceSyncFromPreview(false);
            return;
        }
        hideTxtPreviewSelection();
        clearPreviewDomSelection();
        syncSelectionFromPreviewRef.current = false;
        setHtmlSourceSyncFromPreview(false);
        setTxtEditorSyncRange({ start, end });
    }, [clearPreviewDomSelection, hideTxtPreviewSelection, showHtmlSource]);

    const handleTextareaFocus = useCallback(() => {
        htmlSourceEditingFocusedRef.current = true;
        setHtmlSourceSyncFromPreview(false);
        const ta = descriptionTextareaRef.current;
        if (!ta) {
            return;
        }
        const start = ta.selectionStart ?? 0;
        const end = ta.selectionEnd ?? 0;
        if (start === end) {
            setTxtEditorSyncRange(null);
            clearTxtEditorSyncCssHighlight();
        }
    }, []);

    const restoreHtmlSourceSelection = useCallback(
        (options?: { focus?: boolean }) => {
            const pending = pendingHtmlSourceSelectionRef.current;
            if (!pending) {
                return;
            }
            window.setTimeout(() => {
                const el = descriptionTextareaRef.current;
                if (!el) {
                    return;
                }
                try {
                    const len = (el.value ?? '').length;
                    const start = Math.min(Math.max(0, pending.start), len);
                    const end = Math.min(Math.max(0, pending.end), len);
                    el.setSelectionRange(start, end);
                    if (options?.focus) {
                        el.focus();
                    }
                    updateTxtEditorSyncHighlight();
                } catch {
                    /* ignore */
                }
            }, 0);
        },
        [updateTxtEditorSyncHighlight],
    );

    useLayoutEffect(() => {
        clearTxtEditorSyncCssHighlight();
        const root = txtPreviewRef.current;
        const contentRoot = root?.querySelector('[data-txt-preview-content]') as HTMLElement | null;
        clearTxtEditorSyncMark(contentRoot);

        if (
            !showHtmlSource ||
            !txtEditorSelectionRange ||
            txtEditorSelectionRange.start === txtEditorSelectionRange.end
        ) {
            return;
        }

        if (!contentRoot) {
            return;
        }

        const { start: descStart, end: descEnd } = txtEditorSelectionRange;
        const descSelStart = Math.min(descStart, descEnd);
        const descSelEnd = Math.max(descStart, descEnd);
        const fromPreview = syncSelectionFromPreviewRef.current;
        syncSelectionFromPreviewRef.current = false;

        const ta = descriptionTextareaRef.current;

        if (fromPreview) {
            try {
                if (ta) {
                    const len = (ta.value ?? '').length;
                    const selStart = Math.min(Math.max(0, descSelStart), len);
                    const selEnd = Math.min(Math.max(selStart, descSelEnd), len);
                    ta.setSelectionRange(selStart, selEnd);
                    scrollTextareaToSelection(ta, selStart, selEnd);
                    refreshHtmlSourceSelectionBand({ start: selStart, end: selEnd });
                }
            } catch {
                /* ignore */
            }
            return;
        }

        const range = resolvePreviewRangeFromHtmlSourceSelection(
            value || '',
            descSelStart,
            descSelEnd,
            contentRoot,
        );
        if (!range) {
            return;
        }

        const expectedSlice = (value || '').slice(descSelStart, descSelEnd);
        if (!expectedSlice.trim()) {
            return;
        }

        try {
            wrapRangeWithTxtEditorSyncMark(range);
        } catch {
            /* ignore */
        }
    }, [refreshHtmlSourceSelectionBand, showHtmlSource, txtEditorSelectionRange, value]);

    const applyPreviewSelectionToHtmlSource = useCallback((sourceRange: { start: number; end: number }) => {
        pendingHtmlSourceSelectionRef.current = null;
        const ta = descriptionTextareaRef.current;
        if (!ta) {
            return;
        }
        const len = (ta.value ?? '').length;
        const selStart = Math.min(Math.max(0, sourceRange.start), len);
        const selEnd = Math.min(Math.max(selStart, sourceRange.end), len);
        ta.setSelectionRange(selStart, selEnd);
        scrollTextareaToSelection(ta, selStart, selEnd);
        refreshHtmlSourceSelectionBand({ start: selStart, end: selEnd });
    }, [refreshHtmlSourceSelectionBand]);

    const handleHtmlSourceScroll = useCallback((event: React.UIEvent<HTMLTextAreaElement>) => {
        setHtmlSourceScrollTop(event.currentTarget.scrollTop);
    }, []);

    const isFocusInPreview = useCallback(() => {
        const previewEl = previewContentEditableRef.current;
        const active = document.activeElement;
        return !!previewEl && !!active && (previewEl === active || previewEl.contains(active));
    }, []);

    const updateTxtPreviewSelection = useCallback(() => {
        try {
            const root = txtPreviewRef.current;
            const contentRoot = root?.querySelector('[data-txt-preview-content]') as HTMLElement | null;
            if (!root || !contentRoot) {
                return;
            }
            const sel = window.getSelection();
            if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
                setTxtPreviewSelection(null);
                if (!previewPointerActiveRef.current) {
                    clearPreviewEditorSync();
                }
                return;
            }
            const range = sel.getRangeAt(0);
            const text = sel.toString().trim();
            if (!text) {
                setTxtPreviewSelection(null);
                setTxtEditorSyncRange(null);
                return;
            }

            const anchorNode = sel.anchorNode;
            const focusNode = sel.focusNode;
            const inContent = (node: Node | null) => !!node && contentRoot.contains(node);
            if (!inContent(anchorNode) || !inContent(focusNode)) {
                setTxtPreviewSelection(null);
                setTxtEditorSyncRange(null);
                return;
            }

            clearTxtEditorSyncCssHighlight();

            const sourceRange = resolveHtmlSourceRangeFromPreviewSelection(value || '', contentRoot, range);
            if (showHtmlSource && sourceRange) {
                syncSelectionFromPreviewRef.current = true;
                setHtmlSourceSyncFromPreview(true);
                setTxtEditorSyncRange({ start: sourceRange.start, end: sourceRange.end });
                applyPreviewSelectionToHtmlSource(sourceRange);
            } else {
                setHtmlSourceSyncFromPreview(false);
                setTxtEditorSyncRange(null);
            }

            previewPointerActiveRef.current = false;

            const rect = range.getBoundingClientRect();
            const rootRect = root.getBoundingClientRect();
            setTxtPreviewSelection({
                text,
                top: rect.top - rootRect.top + root.scrollTop,
                left: rect.left - rootRect.left + root.scrollLeft,
            });
        } catch {
            setTxtPreviewSelection(null);
            setTxtEditorSyncRange(null);
        }
    }, [applyPreviewSelectionToHtmlSource, showHtmlSource, value]);

    const handlePreviewDoubleClick = useCallback(() => {
        window.requestAnimationFrame(() => {
            updateTxtPreviewSelection();
        });
    }, [updateTxtPreviewSelection]);

    const focusHtmlSourceAtSyncEnd = useCallback(() => {
        const sync = txtEditorSelectionRangeRef.current;
        const ta = descriptionTextareaRef.current;
        if (!sync || !ta || sync.start === sync.end) {
            return false;
        }
        const len = (ta.value ?? '').length;
        const end = Math.min(Math.max(0, Math.max(sync.start, sync.end)), len);
        ta.focus();
        ta.setSelectionRange(end, end);
        scrollTextareaToSelection(ta, Math.min(sync.start, sync.end), end);
        syncSelectionFromPreviewRef.current = false;
        return true;
    }, []);

    const focusPreviewAtSyncEnd = useCallback(() => {
        const sync = txtEditorSelectionRangeRef.current;
        const contentRoot = previewContentEditableRef.current;
        if (!sync || !contentRoot || sync.start === sync.end) {
            return false;
        }
        const descSelStart = Math.min(sync.start, sync.end);
        const descSelEnd = Math.max(sync.start, sync.end);
        const visible = resolvePreviewVisibleSyncRange(value || '', descSelStart, descSelEnd);
        if (!visible) {
            return false;
        }

        let range = findVisibleTextRangeInRoot(contentRoot, visible.textStart, visible.textEnd);
        if (!range) {
            const domVisible = getVisibleTextFromRoot(contentRoot);
            const slice = (value || '').slice(descSelStart, descSelEnd);
            const { text: sliceVisible } = buildVisibleTextMap(slice);
            const match = findVisibleOffsetsForSelectedText(domVisible, sliceVisible || slice, visible.textStart);
            if (match) {
                range = findVisibleTextRangeInRoot(contentRoot, match.start, match.end);
            }
        }
        if (!range) {
            return false;
        }

        clearTxtEditorSyncMark(contentRoot);
        hideTxtPreviewSelection();
        range.collapse(false);
        try {
            contentRoot.focus();
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
        } catch {
            return false;
        }
        syncSelectionFromPreviewRef.current = false;
        return true;
    }, [hideTxtPreviewSelection, value]);

    const handleTextareaBlur = useCallback(
        (event: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            const target = event.target as HTMLTextAreaElement;
            const previewEl = previewContentEditableRef.current;
            const related = event.relatedTarget;
            const blurToPreview =
                previewEl != null &&
                related instanceof Node &&
                (previewEl === related || previewEl.contains(related));

            if (blurToPreview || previewPointerActiveRef.current || isFocusInPreview()) {
                pendingHtmlSourceSelectionRef.current = null;
                return;
            }

            const start = target.selectionStart;
            const end = target.selectionEnd;
            if (typeof start === 'number' && typeof end === 'number') {
                pendingHtmlSourceSelectionRef.current = { start, end };
            }
            setTxtEditorSyncRange(null);

            const formatted = normalizeTxtEditorHtml(target.value);
            if (formatted !== target.value) {
                onChange(formatted);
            }
            void Promise.resolve(onBlur?.(formatted)).finally(() => {
                htmlSourceEditingFocusedRef.current = false;
                if (isFocusInPreview()) {
                    pendingHtmlSourceSelectionRef.current = null;
                    return;
                }
                restoreHtmlSourceSelection();
            });
        },
        [isFocusInPreview, onBlur, onChange, restoreHtmlSourceSelection],
    );

    const handleTextareaPaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            try {
                const text = e.clipboardData?.getData('text/plain') ?? '';
                if (!text) {
                    return;
                }
                const transformed = replaceYoutubeUrlsWithIframes(text);
                if (transformed === text) {
                    return;
                }
                e.preventDefault();
                applyToTextareaSelection(({ value: currentValue, start, end }) => {
                    const nextValue = currentValue.slice(0, start) + transformed + currentValue.slice(end);
                    const cursor = start + transformed.length;
                    return { nextValue, nextStart: cursor, nextEnd: cursor };
                });
            } catch {
                /* fallback to default paste */
            }
        },
        [applyToTextareaSelection],
    );

    const handleImperative: TxtSplitEditorHandle = useMemo(
        () => ({
            showHtmlSource,
            txtEditorSelectionRangeRef,
            descriptionTextareaRef,
            previewContentEditableRef,
            isPreviewEditableFocused,
            insertTimestamp,
            insertTimestampInPreviewEditable,
            insertTimestampInTextarea,
            togglePreviewSelectionCase,
            toggleTextareaSelectionCase,
            applyPreviewInlineStyle,
            applyTextareaInlineStyle,
            wrapPreviewSelectionCopyable,
            wrapTextareaSelectionCopyable,
            focusHtmlSourceAtSyncEnd,
            focusPreviewAtSyncEnd,
        }),
        [
            showHtmlSource,
            isPreviewEditableFocused,
            insertTimestamp,
            insertTimestampInPreviewEditable,
            insertTimestampInTextarea,
            togglePreviewSelectionCase,
            toggleTextareaSelectionCase,
            applyPreviewInlineStyle,
            applyTextareaInlineStyle,
            wrapPreviewSelectionCopyable,
            wrapTextareaSelectionCopyable,
            focusHtmlSourceAtSyncEnd,
            focusPreviewAtSyncEnd,
        ],
    );

    useEffect(() => {
        if ((!enableColorShortcuts && !enableTimestampShortcut) || disabled) {
            return;
        }

        const isEditorFocused = () => {
            const active = document.activeElement as HTMLElement | null;
            const previewRoot = previewContentEditableRef.current;
            const textareaEl = descriptionTextareaRef.current;
            const inPreview =
                !!previewRoot &&
                !!active &&
                (active === previewRoot || previewRoot.contains(active));
            const inTextarea = !!textareaEl && active === textareaEl;
            return { inPreview, inTextarea, focused: inPreview || inTextarea };
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            const key = event.key.toLowerCase();
            const { inPreview, inTextarea, focused } = isEditorFocused();
            if (!focused) {
                return;
            }

            if (
                enableTimestampShortcut &&
                ((event.altKey && !event.metaKey && !event.ctrlKey && key === 'q') ||
                    (event.metaKey && !event.altKey && !event.ctrlKey && key === 'q'))
            ) {
                event.preventDefault();
                insertTimestamp();
                return;
            }

            if (!enableColorShortcuts || !event.altKey || event.metaKey || event.ctrlKey) {
                return;
            }
            if (!isTxtQuickColorShortcutKey(key)) {
                return;
            }

            event.preventDefault();
            const colorKey = TXT_QUICK_COLOR_KEY_MAP[key];
            if (event.shiftKey) {
                const color = TXT_QUICK_COLOR_PALETTE.text[colorKey];
                if (inPreview) {
                    applyPreviewInlineStyle('color', color);
                } else if (inTextarea) {
                    applyTextareaInlineStyle('color', color);
                }
            } else {
                const color = TXT_QUICK_COLOR_PALETTE.background[colorKey];
                if (inPreview) {
                    applyPreviewInlineStyle('background-color', color);
                } else if (inTextarea) {
                    applyTextareaInlineStyle('background-color', color);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [
        applyPreviewInlineStyle,
        applyTextareaInlineStyle,
        disabled,
        enableColorShortcuts,
        enableTimestampShortcut,
        insertTimestamp,
    ]);

    return {
        showHtmlSource,
        handleToggleHtmlSource,
        insertTimestamp,
        txtPreviewRef,
        previewContentEditableRef,
        descriptionTextareaRef,
        txtPreviewSelection,
        hideTxtPreviewSelection,
        txtEditorSelectionRange,
        htmlSourceStackRef,
        htmlSourceScrollTop,
        showHtmlSourceSyncOverlay,
        htmlSourceSelectionBand,
        htmlSourceTextareaInset,
        updateTxtEditorSyncHighlight,
        handleTextareaFocus,
        handleHtmlSourceScroll,
        updateTxtPreviewSelection,
        handlePreviewDoubleClick,
        handlePreviewMouseDown,
        handlePreviewEditableInput,
        handlePreviewCut,
        handlePreviewEditableFocus,
        handlePreviewEditableBlur,
        handlePreviewKeyDown,
        handlePreviewEditablePaste,
        handleTextareaBlur,
        handleTextareaPaste,
        handleImperative,
    };
}
